import {useEffect, useState} from "react"
import {useLocation, useNavigate, useParams} from "react-router-dom"
import type {OtkDocumentDetail} from "shared"

import {DocumentModal} from "@/components/documents/DocumentModal"
import {getOtkDocumentById} from "@/lib/api/documents"
import {useDocumentsStore} from "@/store/documents"

/**
 * documents.md: "Both routes keep the documents list mounted underneath the
 * document modal" — this renders as the nested `:id` route's element, an
 * `Outlet` sibling of the list, so `DocumentModal` opens above it without
 * unmounting the table underneath. Like `DocumentEditModalRoute`, this
 * fetches the fuller `OtkDocumentDetail` via `getOtkDocumentById` itself on
 * mount rather than reading the list's own already-fetched summary
 * `DocumentRow` row — that summary uses this page's own Ukrainian status
 * vocabulary, not the real backend's `DocumentStatus` enum `DocumentModal`
 * needs for `canDelete`. Closing replaces the route with `base`
 * (`/documents` from the main list, `/documents/search` from
 * advanced-search results), preserving the current query string either way.
 */
export function DocumentModalRoute({ base }: { base: string }) {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const deleteDraft = useDocumentsStore((s) => s.deleteDraft)
  const refreshCounts = useDocumentsStore((s) => s.refreshCounts)

  const [detail, setDetail] = useState<OtkDocumentDetail | null>(null)

  useEffect(() => {
    let cancelled = false
    getOtkDocumentById(id)
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch(() => {
        if (!cancelled) setDetail(null)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <DocumentModal
      canDelete={detail?.status === "DRAFT"}
      detail={detail}
      documentId={id}
      documentNumber={detail?.documentNumber}
      mode="view"
      onClose={() =>
        navigate({ pathname: base, search: location.search }, { replace: true })
      }
      onDelete={() => deleteDraft(id, detail?.version ?? 0)}
      onSigned={(document) => {
        setDetail(document)
        void refreshCounts()
      }}
      version={detail?.version}
    />
  )
}
